import React, { useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  IconButton,
  Modal,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { CloseRounded, GroupsRounded, YouTube } from "@mui/icons-material";

const discordIcon = require("../@shared/assets/icons/discord.webp");
const xIcon = require("../@shared/assets/icons/x.png");

export const SOCIAL_LINKS = [
  {
    key: "discord",
    label: "Discord",
    url: "https://discord.gg/u3qapXHRea",
    icon: discordIcon,
  },
  {
    key: "youtube",
    label: "YouTube",
    url: "https://www.youtube.com/@infinftstudios",
    icon: <YouTube aria-hidden="true" sx={{ fontSize: "2rem" }} />,
  },
  {
    key: "x",
    label: "X",
    url: "https://x.com/Infinftroyale",
    icon: xIcon,
  },
];

export const getConfiguredSocialLinks = (links = SOCIAL_LINKS) =>
  links.filter(({ url }) => typeof url === "string" && url.trim().length > 0);

// The full row is retained at the approved 430x582 mobile viewport. At the
// supported 320x480 floor, the complete group moves behind one modal entry.
export const SOCIAL_LINKS_MIN_VIEWPORT = { width: 320, height: 480 };
const COMPACT_ENTRY_MEDIA_QUERY = "(max-width:399px), (max-height:519px)";

const tileSx = {
  minWidth: 56,
  minHeight: 56,
  px: 0.75,
  py: 0.5,
  border: "3px solid #190a2a",
  borderRadius: "14px",
  color: "#fff",
  background: "linear-gradient(180deg, #5b57ff 0%, #3b37d0 100%)",
  boxShadow: "0 7px 14px rgba(0, 0, 0, 0.32)",
  display: "flex",
  flexDirection: "column",
  gap: 0.25,
  transition: "transform 160ms ease, filter 160ms ease",
  "&:hover": {
    filter: "brightness(1.12)",
    transform: "translateY(-2px)",
  },
  "&.Mui-focusVisible": {
    outline: "3px solid #fff",
    outlineOffset: "3px",
    boxShadow: "0 0 0 6px #190a2a, 0 7px 14px rgba(0, 0, 0, 0.32)",
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
    "&:hover": { transform: "none" },
  },
};

function SocialLink({ link }) {
  return (
    <ButtonBase
      component="a"
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open infiNFT ${link.label}`}
      sx={tileSx}
    >
      {React.isValidElement(link.icon) ? (
        link.icon
      ) : (
        <Box
          component="img"
          src={link.icon}
          alt=""
          aria-hidden="true"
          sx={{ width: 28, height: 28, objectFit: "contain" }}
        />
      )}
      <Typography
        component="span"
        sx={{
          color: "inherit",
          fontSize: "0.68rem",
          fontWeight: 900,
          lineHeight: 1,
          textShadow: "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
          textTransform: "uppercase",
        }}
      >
        {link.label}
      </Typography>
    </ButtonBase>
  );
}

function LinksGroup({ links, labelledBy }) {
  return (
    <Box
      role="group"
      aria-labelledby={labelledBy}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}
    >
      {links.map((link) => (
        <SocialLink key={link.key} link={link} />
      ))}
    </Box>
  );
}

export default function SocialLinksDock({ links = SOCIAL_LINKS }) {
  const configuredLinks = useMemo(() => getConfiguredSocialLinks(links), [links]);
  const useCompactEntry = useMediaQuery(COMPACT_ENTRY_MEDIA_QUERY);
  const [open, setOpen] = useState(false);

  if (configuredLinks.length === 0) return null;

  if (useCompactEntry) {
    return (
      <>
        <ButtonBase
          aria-label="Open community links"
          onClick={() => setOpen(true)}
          sx={{ ...tileSx, minWidth: 92 }}
        >
          <GroupsRounded aria-hidden="true" sx={{ fontSize: "1.7rem" }} />
          <Typography component="span" sx={{ fontSize: "0.62rem", fontWeight: 900 }}>
            COMMUNITY
          </Typography>
        </ButtonBase>
        <Modal
          open={open}
          onClose={() => setOpen(false)}
          aria-labelledby="community-links-title"
        >
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: "min(92vw, 360px)",
              border: "3px solid #190a2a",
              borderRadius: 3,
              background: "linear-gradient(180deg, #20144f 0%, #100a2d 100%)",
              boxShadow: 24,
              px: 2,
              pb: 3,
              pt: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography
                id="community-links-title"
                sx={{ flex: 1, color: "#fff", fontWeight: 900, textAlign: "center" }}
              >
                COMMUNITY
              </Typography>
              <IconButton
                aria-label="Close community links"
                onClick={() => setOpen(false)}
                sx={{ color: "#fff", minWidth: 44, minHeight: 44 }}
              >
                <CloseRounded />
              </IconButton>
            </Box>
            <LinksGroup links={configuredLinks} labelledBy="community-links-title" />
          </Box>
        </Modal>
      </>
    );
  }

  return (
    <Box
      sx={{
        border: "2px solid rgba(25, 10, 42, 0.7)",
        borderRadius: 3,
        backgroundColor: "rgba(16, 10, 45, 0.62)",
        boxShadow: "0 8px 22px rgba(0, 0, 0, 0.24)",
        px: 1,
        py: 0.75,
      }}
    >
      <Typography
        id="dashboard-community-links-title"
        sx={{
          color: "#fff",
          fontSize: "0.62rem",
          fontWeight: 900,
          letterSpacing: "0.1em",
          lineHeight: 1,
          mb: 0.75,
          textAlign: "center",
          textShadow: "0 2px 3px #000",
        }}
      >
        COMMUNITY
      </Typography>
      <LinksGroup
        links={configuredLinks}
        labelledBy="dashboard-community-links-title"
      />
    </Box>
  );
}
